/**
 * A single rail row. One component renders every channel — voice, sms and
 * chat all pass through here, keyed only by `thread.channel` picking an icon
 * out of CHANNEL_ICON — because the rail is channel-agnostic from day one
 * (docs/design-handoff/README.md "Queue rail (pane A)" and
 * communicatorv2.3.md §9).
 *
 * Rebuilt for task 4 (Tossker repaint), README §3: rows are cards now
 * (`rowBorderStyle`/`rowOpacityClass`), the old `voice · client · ticket ·
 * owner` subline is replaced by the thread's own last message, and the
 * ticket/company/owner/stack facts move into a chips row instead.
 */
import { useEffect } from 'react'
import type { CSSProperties, MouseEvent, ReactNode } from 'react'
import type { ThreadSummary } from '@communicator/shared'
import type { Peer } from '../../store'
import { prefersReducedMotion } from '../../lib/motion'
import { Avatar, Icon } from '../ui'
import {
  activateOnKey,
  CHANNEL_ICON,
  channelTileStyle,
  CHANNEL_LABEL,
  CLIENT_RESOLVED_CHIP_BG,
  CLIENT_RESOLVED_CHIP_TEXT,
  UNREAD_DOT_CLASS,
  initialsOf,
  livenessLabel,
  looksLikePhoneNumber,
  ownerChipLabel,
  rowBorderStyle,
  rowOpacityClass,
  showClientResolvedChip,
  stackLinkLabel,
  TIER_CHIP_TONE,
  tierChipLabel,
  waitLabel,
  waitTextClass,
} from './format'

const TONE_BG: Record<'warn', string> = { warn: 'bg-warn-subtle' }
const TONE_TEXT: Record<'warn', string> = { warn: 'text-warn' }

/** All chips are `white-space:nowrap` per README "Queue rail (pane A)". `style` covers the two chips (ticket, per README §3) whose colour has no Tailwind utility and reaches for the raw token instead — see `format.ts`'s `channelTileStyle` doc comment for the same pattern. */
function RailChip({
  bg,
  text,
  style,
  children,
  onClick,
  title,
  testId,
}: {
  bg?: string
  text?: string
  style?: CSSProperties
  children: ReactNode
  onClick?: (e: MouseEvent) => void
  title?: string
  testId?: string
}) {
  const className = ['inline-flex items-center gap-1 whitespace-nowrap rounded-[5px] px-2 py-0.5 text-[10px] font-semibold', bg, text]
    .filter(Boolean)
    .join(' ')
  if (!onClick) {
    return (
      <span className={className} style={style} title={title} data-testid={testId}>
        {children}
      </span>
    )
  }
  return (
    <span
      role="button"
      tabIndex={0}
      className={[className, 'cursor-pointer'].join(' ')}
      style={style}
      title={title}
      data-testid={testId}
      onClick={onClick}
      onKeyDown={(e) => activateOnKey(e, () => onClick(e as unknown as MouseEvent))}
    >
      {children}
    </span>
  )
}

export interface RailRowProps {
  thread: ThreadSummary
  selected: boolean
  peers: Peer[]
  /**
   * The signed-in staff member's own id (task 35, mirrors ThreadHeader's own
   * `selfId` prop) — never included among this row's peer badges. Task 35's
   * beacon reports real activity for whoever's signed in wherever their own
   * console happens to have a thread open; without this exclusion, a
   * dispatcher who has this very thread selected would see her own filled
   * badge pulsing on her own row, which the design explicitly rules out
   * ("the badges are for other people"). Optional (defaults to `null`, i.e.
   * "exclude nobody") only so call sites that never render peers — this
   * file's own channel-glyph tests among them — don't have to thread one
   * through; every real caller (QueueRail) always supplies the signed-in
   * staff member's real id.
   */
  selfId?: string | null
  /**
   * Whether more than one SMS line is configured (task 7, multiple SMS
   * lines) — `(store.lines?.length ?? 0) > 1`, computed by `QueueRail` (this
   * component stays prop-pure, same as `selfId`). Gates the line-label chip
   * below: with one line or none, it never renders even on a thread that
   * carries a `lineLabel` — a single-line deployment's rail must stay
   * pixel-identical to before this task. Optional, defaulting to `false`,
   * for the same reason `selfId` is optional: call sites that predate this
   * task (this file's own channel-glyph tests among them) don't have to
   * thread a new prop through just to keep compiling.
   */
  showLine?: boolean
  /** Count of live threads sharing this thread's ticket, including itself. */
  stackCount: number
  stackOpen: boolean
  /** True while the row is playing its cc-slide entrance. */
  arriving: boolean
  onSelect: (id: string) => void
  onToggleStack: (id: string) => void
  onArrivedAnimationEnd: (id: string) => void
}

export function RailRow({
  thread,
  selected,
  peers,
  selfId = null,
  showLine = false,
  stackCount,
  stackOpen,
  arriving,
  onSelect,
  onToggleStack,
  onArrivedAnimationEnd,
}: RailRowProps) {
  const tierChip = tierChipLabel(thread.tier)
  const showStack = thread.ticket !== null && stackCount > 1
  const rowPeers = peers.filter((p) => p.id !== selfId && (p.typingThreadId === thread.id || p.readingThreadId === thread.id))
  const owner = ownerChipLabel(thread, selfId)

  // Fix round 2 (phase-2 whole-phase review re-review, finding 1): a
  // reduced-motion user's browser never fires `cc-slide`'s `animationend`
  // (styles/index.css sets `animation: none` for exactly this class under
  // `prefers-reduced-motion`), so the `onAnimationEnd` handler below never
  // runs and `thread.id` would sit in `store.arrived` forever. This clears
  // it immediately instead — the row already renders in its settled state
  // either way, so "immediately" and "after the (suppressed) animation"
  // look identical on screen. Does nothing when motion isn't reduced: the
  // real `animationend` handler below still does the work, exactly as
  // before this fix.
  useEffect(() => {
    if (arriving && prefersReducedMotion()) onArrivedAnimationEnd(thread.id)
  }, [arriving, thread.id, onArrivedAnimationEnd])

  return (
    <div
      data-testid={`rail-row-${thread.id}`}
      data-band={thread.band}
      role="button"
      tabIndex={0}
      title={thread.reason}
      onClick={() => onSelect(thread.id)}
      onKeyDown={(e) => activateOnKey(e, () => onSelect(thread.id))}
      onAnimationEnd={(e) => {
        if (e.animationName === 'cc-slide') onArrivedAnimationEnd(thread.id)
      }}
      style={rowBorderStyle(thread.band, selected)}
      className={[
        // README §3: cards, not edge-to-edge rows — 10px side margin, radius
        // 12, `--surface-card`, 11-12px padding, 11px gap between rows (the
        // `mb` here, since QueueRail stacks these with no gap of its own).
        'mx-[10px] mb-[11px] flex cursor-pointer gap-[10px] rounded-card bg-card p-3',
        selected ? 'shadow-sm' : '',
        rowOpacityClass(thread.band),
        arriving ? 'animate-cc-slide' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div
        className="flex h-[22px] w-[22px] flex-none items-center justify-center rounded-[7px]"
        style={channelTileStyle(thread.channel)}
      >
        <Icon name={CHANNEL_ICON[thread.channel]} label={CHANNEL_LABEL[thread.channel]} size={13} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-baseline gap-2">
          {/* WAITING ON YOU. Only when the client spoke last AND this person
              has not opened it since — a thread whose last word was ours is
              not waiting on anybody, and marking those would make the rail a
              wall of dots that everyone learns to ignore.

              The ding fires once and is gone; this is what a tech scanning the
              queue can actually see. Keeps its channel colour per README §3
              ("the unread pulse keeps its channel colour"), now on the
              redesign's own `tk-pulse` animation. */}
          {thread.unread ? (
            <span
              data-testid={`rail-unread-${thread.id}`}
              title="Unread reply from the client"
              aria-label="Unread reply from the client"
              className={['h-[7px] w-[7px] flex-none rounded-full animate-tk-pulse', UNREAD_DOT_CLASS[thread.channel]].join(' ')}
            />
          ) : null}
          <div
            className={[
              'truncate text-[13px] font-semibold text-hi',
              looksLikePhoneNumber(thread.who) ? 'font-mono' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {thread.who}
          </div>
          {thread.ticket !== null && (
            <RailChip
              style={{ background: 'var(--tint-accent)', color: 'var(--on-tint-accent)' }}
              testId={`rail-ticket-${thread.id}`}
            >
              <Icon name="ticket" size={10} />
              {thread.ticket}
            </RailChip>
          )}
          <div className="flex flex-1 items-center justify-end gap-1">
            {rowPeers.map((p) => {
              const typing = p.typingThreadId === thread.id
              return (
                <Avatar
                  key={p.id}
                  initials={initialsOf(p.name)}
                  role={p.role}
                  active={typing}
                  size={20}
                  title={`${p.name} is ${typing ? 'typing' : 'reading'} · ${p.role}`}
                  className={typing ? 'animate-cc-pulse' : ''}
                />
              )
            })}
          </div>
          <div className={['flex-none font-mono text-[11px] whitespace-nowrap', waitTextClass(thread.band)].join(' ')}>
            {waitLabel(thread)}
          </div>
        </div>
        {/* README §3: "the last message, one line, ellipsised" — replaces the
            old `voice · client · ticket · owner` subline. Never a placeholder
            for a thread with no messages yet (packages/shared/src/types.ts's
            `lastMessage` doc comment): render nothing rather than a fake line. */}
        {thread.lastMessage !== null && (
          <div data-testid={`rail-last-message-${thread.id}`} className="truncate text-[11px] text-mid">
            {thread.lastMessage}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-[5px]">
          {tierChip && (
            <RailChip bg={TONE_BG[TIER_CHIP_TONE]} text={TONE_TEXT[TIER_CHIP_TONE]} testId={`rail-tier-${thread.id}`}>
              {tierChip}
            </RailChip>
          )}
          {showClientResolvedChip(thread) && (
            <RailChip
              bg={CLIENT_RESOLVED_CHIP_BG}
              text={CLIENT_RESOLVED_CHIP_TEXT}
              title="The client said they're all set — a signal for a tech to close this out, not a close itself"
              testId={`rail-client-resolved-${thread.id}`}
            >
              Client says done
            </RailChip>
          )}
          {thread.client && (
            // Fix round 2 (finding 12): was `bg-elevated` — wrong slot for a
            // chip on this card (finding 1's rule: raised is for surfaces
            // ABOVE a card, not content on one).
            <RailChip bg="bg-sunken" text="text-mid" testId={`rail-company-${thread.id}`}>
              {thread.client}
            </RailChip>
          )}
          {owner && (
            <RailChip bg="bg-sunken" text="text-mid" testId={`rail-owner-${thread.id}`}>
              {owner === 'Yours' ? null : <Avatar initials={initialsOf(thread.owner!)} size={14} />}
              {owner}
            </RailChip>
          )}
          {showLine && thread.lineLabel && (
            <span data-testid="rail-line-label" className="text-[11px] text-lo whitespace-nowrap">
              {thread.lineLabel}
            </span>
          )}
          {showStack && (
            <span
              role="button"
              tabIndex={0}
              title="Show the other comms on this ticket"
              data-testid={`rail-stack-chip-${thread.id}`}
              onClick={(e) => {
                e.stopPropagation()
                onToggleStack(thread.id)
              }}
              onKeyDown={(e) =>
                activateOnKey(e, () => {
                  onToggleStack(thread.id)
                })
              }
              className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap text-[10px] font-semibold text-cc-blue"
            >
              <Icon name="link-2" size={11} />
              {stackLinkLabel(thread.ticket!, stackCount)}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

export interface StackSubRowProps {
  thread: ThreadSummary
  selected: boolean
  onSelect: (id: string) => void
}

/** A stack-expansion sub-row: channel, liveness, live clock, owner — README "Stack expansion". */
export function StackSubRow({ thread, selected, onSelect }: StackSubRowProps) {
  const isLive = thread.liveness === 'live'
  return (
    <div
      data-testid={`rail-stack-row-${thread.id}`}
      role="button"
      tabIndex={0}
      onClick={() => onSelect(thread.id)}
      onKeyDown={(e) => activateOnKey(e, () => onSelect(thread.id))}
      className={[
        'flex cursor-pointer gap-[10px] border-l-[3px] py-[9px] pl-10 pr-[13px]',
        isLive ? 'border-cc-blue' : 'border-transparent',
        selected ? 'bg-cc-blue-pale' : 'bg-card',
      ].join(' ')}
    >
      <div
        className="flex h-5 w-5 flex-none items-center justify-center rounded-md"
        // Same fill+icon pair as the main row's tile (`channelTileStyle`),
        // for the same reason — never the neutral raised surface, which is a
        // visible grey box on a dark theme, and never the fill/icon from two
        // different colour families (finding 2).
        style={isLive ? { background: 'var(--brand-primary-soft)' } : channelTileStyle(thread.channel)}
      >
        <Icon name={CHANNEL_ICON[thread.channel]} label={CHANNEL_LABEL[thread.channel]} size={11} className={isLive ? 'text-cc-blue-text' : ''} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-baseline justify-between gap-2">
          <div className="truncate text-[11px] font-semibold text-hi">{thread.who}</div>
          <div className={['flex-none whitespace-nowrap font-mono text-[10px]', isLive ? 'text-bad' : 'text-lo'].join(' ')}>
            {waitLabel(thread)}
          </div>
        </div>
        <div className="truncate text-[10px] text-lo">
          {CHANNEL_LABEL[thread.channel]} · {livenessLabel(thread.liveness)} · {thread.owner ?? 'unclaimed'}
        </div>
      </div>
    </div>
  )
}
